'use client'

import { Fragment, type KeyboardEvent, type ReactNode, useState } from 'react'

import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink'
import { VideoPlayerModal } from '@src/components/VideoPlayer/VideoPlayerModal'
import { type UseClickActionArgs } from '@src/hooks/useClickAction/useClickActionArgs'
import { useClickActionContext } from '@src/hooks/useClickAction/useClickActionContext'
import { useOnClick } from '@src/hooks/useClickAction/useOnClick'
import { useLocale } from '@src/i18n/LocaleProvider'
import { filterBuilderAttributes, getBuilderPreviewMode } from '@src/utils/builder.util'

import { LoginModal } from './LoginModal'

export const useClickAction = <T extends UseClickActionArgs = UseClickActionArgs>(args: T) => {
  const { actionName = 'none', popoutVideo, navigate, attributes } = args
  const { locale } = useLocale()
  const videoId = popoutVideo?.videoId
  const { edit } = getBuilderPreviewMode()
  const [isLoading, setIsLoading] = useState(false)
  const [isVideoOpen, setVideoOpen] = useState(false)
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false)
  const { isLoadingAnyAction } = useClickActionContext()

  const shouldSkipOnClick = edit || isLoading || isLoadingAnyAction || actionName === 'none'

  const { onClick } = useOnClick({
    setVideoOpen,
    setIsLoginModalOpen,
    setIsLoading,
    shouldSkip: shouldSkipOnClick,
    useClickActionArgs: args,
  })

  const getLoginModalRedirectUrl = () => {
    const currentUrl = new URL(window.location.href)
    currentUrl.pathname = `${locale.path}${args.loginModal?.redirectUrl}`
    return currentUrl.toString()
  }

  const onKeyDown = async (e: KeyboardEvent) => {
    if (e.code === 'Enter' || e.code === 'Space') {
      await onClick?.()
    }
  }

  const isWrapping = actionName === 'navigate'

  const renderVideo = () => {
    return (
      <>
        {videoId && (
          <VideoPlayerModal
            autoplay
            isOpen={isVideoOpen}
            onClose={() => {
              setVideoOpen(false)
            }}
            videoId={videoId}
          />
        )}
      </>
    )
  }

  const renderLoginModal = () => {
    return (
      <>
        {args.loginModal?.redirectUrl && (
          <LoginModal
            redirectUrl={getLoginModalRedirectUrl()}
            heading={args.loginModal?.heading}
            subHeading={args.loginModal?.subHeading}
            disclaimer={args.loginModal?.disclaimer}
            isOpen={isLoginModalOpen || Boolean(edit && args.loginModal?.isPreviewing)}
            onClose={() => setIsLoginModalOpen(false)}
          />
        )}
      </>
    )
  }

  const ClickWrapper = ({ children }: { children: ReactNode }) => {
    const usingCartFlyout = navigate?.href === '/cart'
    const wrapperProps =
      !isWrapping || usingCartFlyout
        ? {}
        : {
            ...(filterBuilderAttributes(attributes) as any),
            href: String(navigate?.href),
            target: navigate?.target,
            onClick,
            onKeyDown,
          }

    const Wrapper = isWrapping && !usingCartFlyout ? LocalizedLink : Fragment
    return (
      <Wrapper {...wrapperProps}>
        {children}
        {renderVideo()}
        {renderLoginModal()}
      </Wrapper>
    )
  }

  const handlers = shouldSkipOnClick
    ? {}
    : {
        onClick,
        onKeyDown,
      }

  return {
    isWrapping,
    handlers,
    isLoading,
    ClickWrapper,
    renderVideo,
  }
}
